/**
 * OrgMemberManager - super-admin member management for an organization.
 *
 * Renders the member list with an inline role dropdown and remove button per
 * member, plus an "add member" row (single-user search + role). All mutations
 * call back through `onChanged` so the parent can refetch org details.
 *
 * The organization creator cannot be removed (enforced on the server too).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Search, X, Loader2, UserPlus } from 'lucide-react'
import PropTypes from 'prop-types'
import toast from 'react-hot-toast'
import { useDebounce } from '../../hooks/useDebounce'
import { ORG_ROLES } from '../../utils/orgRoles'
import organizationAdminService from '../../services/organizationAdminService'
import api from '../../services/api'
import { createLogger } from '../../utils/logger'

const logger = createLogger('OrgMemberManager')

const memberName = (user) => {
  if (!user) return 'Unknown User'
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`
  return user.email || 'Unknown User'
}

const OrgMemberManager = ({ organization, onChanged }) => {
  const orgId = organization?.id
  const creatorId = organization?.createdBy?.id
  // Stable reference per organization so runSearch (which filters out current
  // members) only changes when the member list actually changes.
  const members = useMemo(() => organization?.members || [], [organization])

  // Add-member search state
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newRole, setNewRole] = useState('student')
  const [adding, setAdding] = useState(false)
  const [busyMembershipId, setBusyMembershipId] = useState(null)
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null)
  const containerRef = useRef(null)
  const searchAbortRef = useRef(null)

  const debouncedSearch = useDebounce(search, 300)

  const runSearch = useCallback(
    async (query) => {
      // Cancel any in-flight search so a slower earlier response can't clobber a
      // newer one (out-of-order results when typing quickly).
      if (searchAbortRef.current) searchAbortRef.current.abort()
      const controller = new AbortController()
      searchAbortRef.current = controller

      try {
        setSearching(true)
        const { data } = await api.get('/admin/users', {
          params: { search: query, limit: 10 },
          signal: controller.signal
        })
        const existingIds = new Set(members.map((m) => m.user?.id))
        setResults((data.users || []).filter((u) => !existingIds.has(u.id)))
        setShowDropdown(true)
      } catch (error) {
        // Ignore aborts (superseded by a newer search); surface real errors.
        if (error.code !== 'ERR_CANCELED' && error.name !== 'CanceledError') {
          logger.error('Error searching users:', error)
        }
      } finally {
        // Only clear the spinner if this is still the latest request.
        if (searchAbortRef.current === controller) setSearching(false)
      }
    },
    [members]
  )

  useEffect(() => {
    if (debouncedSearch.trim().length >= 2 && !selectedUser) {
      runSearch(debouncedSearch.trim())
    } else {
      setResults([])
    }
  }, [debouncedSearch, selectedUser, runSearch])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const pickUser = (user) => {
    setSelectedUser(user)
    setSearch(user.email || memberName(user))
    setResults([])
    setShowDropdown(false)
  }

  const clearSelectedUser = () => {
    setSelectedUser(null)
    setSearch('')
    setResults([])
  }

  const handleAdd = async () => {
    if (!selectedUser || !orgId) return
    try {
      setAdding(true)
      await organizationAdminService.adminAddMember(orgId, {
        userId: selectedUser.id,
        role: newRole
      })
      toast.success(`Added ${memberName(selectedUser)} as ${newRole}`)
      clearSelectedUser()
      setNewRole('student')
      onChanged?.()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add member')
    } finally {
      setAdding(false)
    }
  }

  const handleRoleChange = async (membershipId, role) => {
    if (!orgId) return
    try {
      setBusyMembershipId(membershipId)
      await organizationAdminService.adminUpdateMemberRole(orgId, membershipId, role)
      toast.success('Role updated')
      onChanged?.()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update role')
    } finally {
      setBusyMembershipId(null)
    }
  }

  const handleRemove = async (member) => {
    if (!orgId) return
    try {
      setBusyMembershipId(member.id)
      await organizationAdminService.adminRemoveMember(orgId, member.id)
      toast.success(`Removed ${memberName(member.user)}`)
      onChanged?.()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove member')
    } finally {
      setBusyMembershipId(null)
      setConfirmingRemoveId(null)
    }
  }

  return (
    <div>
      <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Members ({members.length})
      </h5>

      {/* Add member row */}
      <div
        ref={containerRef}
        className="mb-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
      >
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {searching ? (
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              ) : (
                <Search className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                if (selectedUser) setSelectedUser(null)
              }}
              onFocus={() => results.length > 0 && setShowDropdown(true)}
              placeholder="Search user by name or email..."
              className="w-full pl-10 pr-9 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {selectedUser && (
              <button
                type="button"
                onClick={clearSelectedUser}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500"
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {showDropdown && results.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
                {results.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => pickUser(user)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {memberName(user)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {ORG_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleAdd}
            disabled={!selectedUser || adding}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {adding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            Add
          </button>
        </div>
      </div>

      {/* Member list */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {members.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {members.map((member) => {
              const isCreator = member.user?.id && member.user.id === creatorId
              const busy = busyMembershipId === member.id
              return (
                <div key={member.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {member.user?.firstName && member.user?.lastName
                              ? `${member.user.firstName.charAt(0)}${member.user.lastName.charAt(0)}`
                              : member.user?.email?.substring(0, 2).toUpperCase() || '??'}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {memberName(member.user)}
                          {isCreator && (
                            <span className="ml-2 text-xs text-gray-400">(creator)</span>
                          )}
                        </p>
                        {member.user?.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {member.user.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <select
                        value={member.role}
                        disabled={busy}
                        onChange={(e) => handleRoleChange(member.id, e.target.value)}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                      >
                        {ORG_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                        {/* Preserve any non-standard existing role as a valid option */}
                        {!ORG_ROLES.some((r) => r.value === member.role) && (
                          <option value={member.role}>{member.role}</option>
                        )}
                      </select>
                      {confirmingRemoveId === member.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleRemove(member)}
                            disabled={busy}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Remove'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingRemoveId(null)}
                            disabled={busy}
                            className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingRemoveId(member.id)}
                          disabled={busy || isCreator}
                          title={
                            isCreator
                              ? 'The organization creator cannot be removed'
                              : 'Remove member'
                          }
                          className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
            No members found
          </div>
        )}
      </div>
    </div>
  )
}

OrgMemberManager.propTypes = {
  organization: PropTypes.object,
  onChanged: PropTypes.func
}

export default OrgMemberManager
